import React, { useEffect, useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import api from "@/lib/api";
import { ConferenceStat, ConferenceStatus, inr } from "./ConferenceShared";

export default function ConferenceOverview({ dashboard, isState, onDistrictOpen }) {
  const financial = dashboard?.financial;
  const rows = financial?.districts || [];
  const total = financial?.total || financial || {};
  const chartRows = useMemo(() => rows.map(r => ({
    district: r.district,
    Target: r.target,
    Collected: r.submitted_collected,
    Verified: r.verified_collected,
    Transferred: r.state_transferred,
    Outstanding: r.state_outstanding,
    Surplus: r.district_surplus_available,
  })), [rows]);
  const [performanceRows, setPerformanceRows] = useState([]);

  useEffect(() => {
    if (!isState) return;
    api.get("/conference/reports/performance")
      .then(r => setPerformanceRows(r.data.rows || []))
      .catch(() => setPerformanceRows([]));
  }, [isState, dashboard?.generated_at, dashboard?.financial?.total?.verified_collected]);

  if (!dashboard) return <Card className="p-8 text-center text-muted-foreground">Loading conference dashboard…</Card>;

  return (
    <div className="space-y-5" data-testid="conference-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ConferenceStat testId="conf-stat-members" label="Paid & Active Members / പെയ്ഡ് & ആക്ടീവ് അംഗങ്ങൾ" value={isState ? total.members : financial?.members} tone="blue" />
        <ConferenceStat testId="conf-stat-target" label="State Target / സംസ്ഥാന ലക്ഷ്യം" value={inr(isState ? total.target : financial?.target)} tone="orange" />
        <ConferenceStat testId="conf-stat-collected" label="Submitted + Verified / ആകെ പിരിവ്" value={inr(isState ? total.submitted_collected : financial?.submitted_collected)} tone="blue" />
        <ConferenceStat testId="conf-stat-verified" label="Verified Collection / പരിശോധിച്ച തുക" value={inr(isState ? total.verified_collected : financial?.verified_collected)} tone="green" />
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ConferenceStat testId="conf-stat-transferred" label="Transferred to State" value={inr(isState ? total.state_transferred : financial?.state_transferred)} />
        <ConferenceStat testId="conf-stat-outstanding" label="State Outstanding" value={inr(isState ? total.state_outstanding : financial?.state_outstanding)} tone="red" />
        <ConferenceStat testId="conf-stat-surplus" label="District Surplus Available" value={inr(isState ? total.district_surplus_available : financial?.district_surplus_available)} tone="green" />
        <ConferenceStat testId="conf-stat-balance" label="District Balance" value={inr(isState ? total.district_balance : financial?.district_balance)} tone="orange" />
      </div>

      {isState ? (
        <Card className="p-5">
          <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
            <div>
              <h3 className="font-black text-lg">District-wise Conference Progress</h3>
              <p className="text-sm text-muted-foreground">Live targets and collections from the existing KELTA membership database.</p>
            </div>
            <Badge variant="secondary" data-testid="conf-live-membership-badge">Live membership source</Badge>
          </div>
          <div className="h-72" data-testid="conf-district-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartRows} margin={{ top: 10, right: 10, left: 0, bottom: 55 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="district" angle={-45} textAnchor="end" interval={0} height={70} tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={v => `₹${Math.round(v / 1000)}k`} />
                <Tooltip formatter={v => inr(v)} />
                <Bar dataKey="Target" fill="#94A3B8" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Collected" fill="#0D4FB5" radius={[4, 4, 0, 0]} />
                <Bar dataKey="Verified" fill="#238B2E" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      ) : null}

      {isState ? (
        <div className="grid xl:grid-cols-3 gap-4">
          <Card className="p-4">
            <h4 className="font-bold mb-3">State Transfer Progress</h4>
            <div className="h-56"><ResponsiveContainer width="100%" height="100%"><BarChart data={chartRows}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="district" hide /><YAxis tickFormatter={v => `₹${Math.round(v / 1000)}k`} /><Tooltip formatter={v => inr(v)} /><Bar dataKey="Transferred" fill="#238B2E" radius={[4, 4, 0, 0]} /><Bar dataKey="Outstanding" fill="#EF4444" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
          </Card>
          <Card className="p-4">
            <h4 className="font-bold mb-3">District Surplus Comparison</h4>
            <div className="h-56"><ResponsiveContainer width="100%" height="100%"><BarChart data={chartRows}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="district" hide /><YAxis tickFormatter={v => `₹${Math.round(v / 1000)}k`} /><Tooltip formatter={v => inr(v)} /><Bar dataKey="Surplus" fill="#F68B1F" radius={[4, 4, 0, 0]} /></BarChart></ResponsiveContainer></div>
          </Card>
          <Card className="p-4" data-testid="conf-member-performance">
            <h4 className="font-bold mb-3">Top Member Collections (Verified)</h4>
            <div className="space-y-2 max-h-56 overflow-auto">{performanceRows.slice(0, 8).map((r, i) => <div key={`${r[1]}-${i}`} className="rounded-lg bg-muted/50 p-2"><div className="flex justify-between gap-2 text-xs"><span className="font-semibold truncate">{r[0]}</span><span className="font-black text-brand-green">{inr(r[5])}</span></div><div className="text-[10px] text-muted-foreground">{r[1]} · {r[2]}</div></div>)}</div>
          </Card>
        </div>
      ) : null}

      {isState ? (
        <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
          {rows.map(r => {
            const pct = r.target ? Math.min((r.submitted_collected / r.target) * 100, 100) : 0;
            return (
              <button
                type="button"
                key={r.district}
                onClick={() => onDistrictOpen?.(r.district)}
                className="text-left rounded-xl border border-border bg-card p-4 hover:-translate-y-0.5 hover:shadow-md transition-[transform,box-shadow,border-color] hover:border-brand-blue/40"
                data-testid={`conf-district-card-${r.district.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-bold">{r.district}</div>
                    <div className="text-xs text-muted-foreground">{r.members} members · target {inr(r.target)}</div>
                  </div>
                  <ConferenceStatus value={r.status} />
                </div>
                <Progress value={pct} className="mt-4 h-2" />
                <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
                  <div><div className="text-muted-foreground">Collected</div><div className="font-bold">{inr(r.submitted_collected)}</div></div>
                  <div><div className="text-muted-foreground">Transferred</div><div className="font-bold">{inr(r.state_transferred)}</div></div>
                  <div><div className="text-muted-foreground">Surplus</div><div className="font-bold">{inr(r.district_surplus_available)}</div></div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <Card className="p-5" data-testid="conf-district-progress-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-black text-lg">{dashboard.district} District Progress</h3>
              <p className="text-sm text-muted-foreground">First ₹2,000 per eligible member is the State Conference contribution target.</p>
            </div>
            <ConferenceStatus value={financial?.status} />
          </div>
          <Progress value={financial?.target ? Math.min((financial.submitted_collected / financial.target) * 100, 100) : 0} className="mt-4 h-3" />
          <div className="grid sm:grid-cols-4 gap-3 mt-4 text-sm">
            <Mini k="Members started" v={financial?.members_started} />
            <Mini k="Completed ₹2,000" v={financial?.members_completed} />
            <Mini k="Outstanding members" v={financial?.members_outstanding} />
            <Mini k="Receipts used" v={financial?.receipts_used} />
          </div>
        </Card>
      )}
    </div>
  );
}

function Mini({ k, v }) {
  return <div className="rounded-lg bg-muted/50 p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{k}</div><div className="font-black text-lg">{v ?? 0}</div></div>;
}
